import React, { useState, useMemo } from 'react';
import { OutlierConfigType } from '../../../../services/ConfigurationService/types/centerType';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Page, Sort, RowSelectEventArgs } from '@syncfusion/ej2-react-grids';
import { Box, Typography, TextField, Stack, IconButton } from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';

interface OutlierConfigGridProps {
  outlierConfigs: OutlierConfigType[];
  onRowSelect?: (config: OutlierConfigType) => void;
}

interface OutlierConfigGridRow {
  id: string;
  effectiveDate: string;
  hospitalMultipleThreshold: number;
  fixedDollarThreshold: number;
  outlierPaymentPercentage: number;
  costToChargeRatio: number;
}

export function OutlierConfigGrid({ outlierConfigs, onRowSelect }: OutlierConfigGridProps) {
  const [searchTerm, setSearchTerm] = useState('');

  // Filter outlier configs based on search term
  const filteredConfigs = useMemo(() => {
    if (!searchTerm.trim()) {
      return outlierConfigs;
    }

    const searchLower = searchTerm.toLowerCase();
    return outlierConfigs.filter(config =>
      config.effectiveDate.toLowerCase().includes(searchLower) ||
      config.hospitalMultipleThreshold.toString().includes(searchLower) ||
      config.fixedDollarThreshold.toString().includes(searchLower) ||
      config.outlierPaymentPercentage.toString().includes(searchLower) ||
      config.costToChargeRatio.toString().includes(searchLower)
    );
  }, [outlierConfigs, searchTerm]);

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  // Handle row selection for editing
  const handleRowSelection = (args: RowSelectEventArgs) => {
    if (args.data && onRowSelect) {
      const selectedRow = args.data as OutlierConfigGridRow;
      const config = outlierConfigs.find(c => c.id === selectedRow.id);
      if (config) {
        onRowSelect(config);
      }
    }
  };

  // Format date for display - parse directly to avoid UTC timezone shift
  const dateTemplate = (props: OutlierConfigGridRow) => {
    if (!props.effectiveDate) return '';
    const [datePart] = props.effectiveDate.split('T');
    const [year, month, day] = datePart.split('-');
    return `${month}/${day}/${year}`;
  };

  const searchBox = (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <SearchIcon sx={{ color: '#64748b' }} />
        <TextField
          fullWidth
          size="small"
          placeholder="Search outlier configurations..."
          value={searchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#ffffff',
              '& fieldset': {
                borderColor: '#e2e8f0',
              },
              '&:hover fieldset': {
                borderColor: '#3b82f6',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#3b82f6',
              }
            }
          }}
        />
        {searchTerm && (
          <IconButton
            size="small"
            onClick={handleClearSearch}
            sx={{ color: '#64748b' }}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
    </Box>
  );

  if (outlierConfigs.length === 0) {
    return (
      <Box>
        {searchBox}
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
            No outlier configurations found
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            No outlier configurations set for this center
          </Typography>
        </Box>
      </Box>
    );
  }

  if (filteredConfigs.length === 0 && searchTerm.trim()) {
    return (
      <Box>
        {searchBox}
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
            No outlier configurations match your search
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            Try adjusting your search term: &quot;{searchTerm}&quot;
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {searchBox}

      {/* Grid */}
      <GridComponent
        dataSource={filteredConfigs}
        height={400}
        allowPaging={true}
        allowSorting={true}
        pageSettings={{ pageSize: 25 }}
        selectionSettings={{ mode: 'Row', type: 'Single' }}
        rowSelected={handleRowSelection}
        style={{
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          backgroundColor: '#ffffff'
        }}
        enableHover={true}
      >
        <ColumnsDirective>
          <ColumnDirective
            field="effectiveDate"
            headerText="Effective Date"
            width="140"
            textAlign="Left"
            template={dateTemplate}
          />
          <ColumnDirective
            field="hospitalMultipleThreshold"
            headerText="Hospital Multiple Threshold"
            width="180"
            textAlign="Right"
            type="number"
            format="N4"
          />
          <ColumnDirective
            field="fixedDollarThreshold"
            headerText="Fixed Dollar Threshold"
            width="160"
            textAlign="Right"
            type="number"
            format="C2"
          />
          <ColumnDirective
            field="outlierPaymentPercentage"
            headerText="Outlier Payment %"
            width="150"
            textAlign="Right"
            type="number"
            format="P4"
          />
          <ColumnDirective
            field="costToChargeRatio"
            headerText="Cost-to-Charge Ratio"
            width="160"
            textAlign="Right"
            type="number"
            format="N4"
          />
        </ColumnsDirective>
        <Inject services={[Page, Sort]} />
      </GridComponent>
    </Box>
  );
}
